import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useAnalytics } from "@/hooks/useAnalytics"
import { motion } from "framer-motion"
import { CalendarDays, Flame, ChevronRight } from "lucide-react"
import { getPlanForGoal, getMealIdeaForNow } from "@/data/nutritionPlans"
import { BottomNav } from "@/components/BottomNav"
import { IconTile, InfoBanner, NutritionBackdrop, NutritionHeader, PlanPill, SectionLabel } from "@/components/nutrition/NutritionUI"
import { CARD, CARD_PRESSABLE, MACRO_COLORS, NUTRITION_VIOLET, haloSurface } from "@/components/nutrition/theme"
import { cn, formatIntFr } from "@/lib/utils"

interface NutritionPageProps {
  email: string
  firstName?: string
  goalId?: string
}

const MACRO_ROWS = [
  { key: "protein", label: "Protéines", emoji: "🥚" },
  { key: "carbs", label: "Glucides", emoji: "🥞" },
  { key: "fat", label: "Lipides", emoji: "🥑" },
] as const

export const NutritionPage = ({ email: _email, firstName: _firstName, goalId }: NutritionPageProps) => {
  const navigate = useNavigate()

  const { track } = useAnalytics()
  const hasTrackedView = useRef(false)
  useEffect(() => {
    if (!hasTrackedView.current) {
      track('nutrition_viewed', { goal_id: goalId })
      hasTrackedView.current = true
    }
  }, [track])

  const plan = getPlanForGoal(goalId)
  const { meal: mealLabel, idea: mealIdea } = getMealIdeaForNow(plan.id)

  return (
    <div className="min-h-screen bg-[#060a13] pb-32 cursor-default relative">
      <NutritionBackdrop />

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 relative z-10">
        <NutritionHeader title="Apport énergétique" onBack={() => navigate("/dashboard")}>
          <PlanPill label={plan.label} />
        </NutritionHeader>
      </motion.div>

      {/* OBJECTIF CALORIQUE */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="px-4 mt-5 relative z-10">
        <div className="relative overflow-hidden rounded-3xl border px-5 pt-5 pb-6 text-center" style={haloSurface("50% -10%")}>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-orange-300">Objectif journalier</span>
          </span>
          <div
            className="mt-4 text-[60px] font-black leading-none tracking-tight tabular-nums bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(180deg, #c4b5fd 0%, #8b5cf6 100%)" }}
          >
            {formatIntFr(plan.dailyCalories)}
          </div>
          <p className="mt-2.5 font-mono text-[13px] text-gray-400">kcal / jour</p>
          <p className="mt-4 text-[13px] text-gray-400 leading-relaxed max-w-[300px] mx-auto">
            {plan.description}
          </p>
        </div>
      </motion.div>

      {/* MACROS */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 mt-6 relative z-10">
        <SectionLabel>Répartition</SectionLabel>
        <div className={cn(CARD, "px-4 py-1.5")}>
          {MACRO_ROWS.map(({ key, label, emoji }) => (
            <MacroRow
              key={key}
              emoji={emoji}
              label={label}
              grams={plan.macros[key]}
              pct={plan.macrosPct[key]}
              color={MACRO_COLORS[key]}
            />
          ))}
        </div>
      </motion.div>

      {/* IDÉE DE REPAS — carte informative, non cliquable (pas de chevron) */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="px-4 mt-6 relative z-10">
        <SectionLabel>Idée pour {mealLabel.toLowerCase()}</SectionLabel>
        <div className={cn(CARD, "flex items-center gap-4 p-4")}>
          <div className="w-14 h-14 rounded-2xl border border-white/[0.08] bg-white/[0.04] flex items-center justify-center shrink-0 text-[32px] leading-none">
            {mealIdea.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-white">{mealIdea.title}</h3>
            <p className="text-[12px] text-gray-400 mt-1 leading-relaxed">{mealIdea.description}</p>
          </div>
        </div>
      </motion.div>

      {/* VOIR LA SEMAINE */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="px-4 mt-4 relative z-10">
        <button
          type="button"
          onClick={() => navigate("/dashboard/nutrition/week")}
          className={cn(CARD_PRESSABLE, "w-full flex items-center gap-3.5 rounded-2xl border border-violet-400/30 px-4 py-4 text-left hover:border-violet-400/50")}
          style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.16), rgba(139,92,246,0.04) 70%)" }}
        >
          <IconTile color={NUTRITION_VIOLET}>
            <CalendarDays className="w-[18px] h-[18px] text-violet-300" />
          </IconTile>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold text-white">Voir la semaine complète</div>
            <div className="font-mono text-[11px] text-gray-400 mt-0.5">7 jours · 4 repas par jour</div>
          </div>
          <ChevronRight className="w-4 h-4 text-violet-300 shrink-0" />
        </button>
      </motion.div>

      {/* INFO PLAN */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="px-4 mt-4 relative z-10">
        <InfoBanner>
          <span className="text-amber-400 font-semibold">Bon à savoir</span> · ce plan est adapté à ton objectif actuel. Si tu changes d'objectif dans ton profil, le plan nutritionnel s'ajustera automatiquement.
        </InfoBanner>
      </motion.div>

      <BottomNav />
    </div>
  )
}

// ============================================
// MACRO ROW
// ============================================

interface MacroRowProps {
  emoji: string
  label: string
  grams: number
  pct: number
  color: string
}

// Barre = part de la macro dans l'apport énergétique (les trois barres totalisent 100 %).
function MacroRow({ emoji, label, grams, pct, color }: MacroRowProps) {
  return (
    <div className="flex items-center gap-3.5 py-3">
      <IconTile color={color} className="text-[18px] leading-none">{emoji}</IconTile>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[14px] font-semibold text-white">{label}</span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[15px] font-bold tabular-nums" style={{ color }}>{grams}g</span>
            <span className="font-mono text-[10.5px] text-gray-500">({pct}%)</span>
          </div>
        </div>
        <div className="h-[5px] rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-full rounded-full"
            style={{ background: color, boxShadow: `0 0 8px ${color}66` }}
          />
        </div>
      </div>
    </div>
  )
}
